import {
  Checkbox,
  Icons,
  SearchField,
  Tag,
  TextField,
  Typography,
} from "@flash-tecnologia/hros-web-ui-v2";
import { Box } from "src/components/core-components/atoms/Box/box";
import {
  Dropdown,
  DropdownModes,
  Sizes as DropdownSizes,
} from "src/components/core-components/organisms/Dropdown/dropdown";
import { useTheme } from "styled-components";

import { MaskedScrollArea } from "@/components/core-components/organisms/MaskedScrollArea/masked-scroll-area";
import { registrationNumberFormatter } from "@/utils/registration-number-formatter";
import { GenerateContractLoadingModal } from "../../components/GenerateContractLoadingModal/generate-contract-loading-modal";
import { ManageMembersLoading } from "../../components/ManageMembersLoading/manage-members-loading";
import { useManageCompaniesForm } from "../../context/manage-companies-context";
import { AddedCompany } from "./components/AddedCompany/added-company";
import { DeleteCompanyModal } from "./components/DeleteCompanyModal/delete-company-modal";
import * as SC from "./manage-companies.styles";
import { useParams } from "react-router-dom";
import { useSelectedCompany } from "@flash-tecnologia/hros-web-utility";
import { useUpdateStepStatus } from "@/hooks";
import { useEffect } from "react";
import { StepKeys } from "../../constants";

export const ManageCompanies = () => {
  const theme = useTheme();
  const { setupId } = useParams();
  const { selectedCompany } = useSelectedCompany();
  const { updateStepStatus } = useUpdateStepStatus(selectedCompany.id);

  const {
    addCompanyLoading,
    address,
    errors,
    filteredCompanies: filteredAddedCompanies = [],
    isDeleteCompanyModalOpen,
    isFormDisabled,
    isFormLoading,
    isGenerateContractLoadingModalOpen,
    legalName,
    name,
    registrationNumber,
    termsAccepted,
    totalCompaniesAdded,
    onAddCompanyClick,
    onAddressComplementChange,
    onAddressNumberBlur,
    onAddressNumberChange,
    onDeleteCompanyClick,
    onDeleteCompanyModalClose,
    onDeleteCompanySubmit,
    onGenerateContractLoadingModalClose,
    onGetContractClick,
    onLegalNameBlur,
    onLegalNameChange,
    onNameChange,
    onRegistrationNumberBlur,
    onRegistrationNumberChange,
    onSearchCompaniesTermChange,
    onTermsAcceptedChange,
    onZipCodeBlur,
    onZipCodeChange,
  } = useManageCompaniesForm();

  useEffect(() => {
    if (setupId) {
      updateStepStatus({
        setupId,
        externalId: StepKeys.ADD_COMPANIES,
        status: "DOING",
      });
    }
  }, [setupId, updateStepStatus]);

  if (isFormLoading) {
    return <ManageMembersLoading />;
  }

  const isAddressSectionVisible =
    address.city || address.street || address.state || address.district;

  return (
    <>
      <SC.ResponsiveContainer>
        <SC.Grid>
          <SC.LeftSection>
            <MaskedScrollArea>
              <Box flexDirection="column" gap={"s"} $px="xs2">
                <Typography.Headline7 weight={700} color="neutral.20">
                  Adicione abaixo as empresas do grupo
                </Typography.Headline7>
                <Box flexDirection="column" gap={"xs"}>
                  <Box gap={"xs"} flex={1} alignItems="stretch">
                    <Box flex={0.3} flexDirection="column">
                      <TextField
                        fullWidth
                        onBlur={onRegistrationNumberBlur}
                        onChange={(event) =>
                          onRegistrationNumberChange(event.target.value)
                        }
                        label={"CNPJ*"}
                        error={!!errors?.registrationNumber}
                        helperText={errors?.registrationNumber}
                        value={
                          registrationNumber &&
                          registrationNumberFormatter(registrationNumber)
                        }
                      />
                    </Box>
                    <Box flex={0.7} flexDirection="column">
                      <TextField
                        fullWidth
                        onBlur={onLegalNameBlur}
                        onChange={(event) =>
                          onLegalNameChange(event.target.value)
                        }
                        label={"Razão Social*"}
                        error={!isFormDisabled && !!errors?.legalName}
                        helperText={!isFormDisabled && errors?.legalName}
                        value={legalName}
                        disabled={isFormDisabled}
                      />
                    </Box>
                  </Box>
                  <Box gap={"xs"} flex={1} alignItems="stretch">
                    <Box flex={0.7} flexDirection="column">
                      <TextField
                        fullWidth
                        onChange={(event) => onNameChange(event.target.value)}
                        label={"Nome fantasia (opcional)"}
                        value={name}
                        disabled={isFormDisabled}
                      />
                    </Box>
                    <Box flex={0.3}>
                      <Box gap={"xs4"} flex={1} alignItems="flex-start">
                        <div>
                          <TextField
                            type="text"
                            placeholder="XXXXX-XXX"
                            inputProps={{
                              maxLength: 9,
                            }}
                            imaskProps={{
                              mask: "00000-000",
                            }}
                            inputMode="numeric"
                            disabled={isFormDisabled}
                            fullWidth
                            onBlur={onZipCodeBlur}
                            onChange={(event) =>
                              onZipCodeChange(event.target.value)
                            }
                            label={"CEP*"}
                            error={
                              !isFormDisabled && !!errors?.address?.zipCode
                            }
                            helperText={
                              !isFormDisabled && errors?.address?.zipCode
                            }
                            size="medium"
                            value={address.zipCode}
                          />
                        </div>
                      </Box>
                    </Box>
                  </Box>
                  {isAddressSectionVisible && (
                    <SC.AddressSectionAnimation>
                      <TextField
                        value={address?.street}
                        label="Endereço*"
                        disabled
                      />
                      <Box gap={"xs"} flex={1} alignItems="stretch">
                        <Box flex={0.4} flexDirection="column">
                          <Dropdown.Root
                            label={"Bairro*"}
                            mode={DropdownModes.SingleSelect}
                            size={DropdownSizes.Md}
                            disabled
                            selectedOption={
                              address.district
                                ? {
                                    id: address.district,
                                    label: address.district,
                                    value: address.district,
                                  }
                                : null
                            }
                          />
                        </Box>
                        <Box flex={0.4} flexDirection="column">
                          <Dropdown.Root
                            label={"Cidade*"}
                            mode={DropdownModes.SingleSelect}
                            size={DropdownSizes.Md}
                            disabled
                            selectedOption={
                              address.city
                                ? {
                                    id: address.city,
                                    label: address.city,
                                    value: address.city,
                                  }
                                : null
                            }
                          />
                        </Box>
                        <Box flex={0.2} flexDirection="column">
                          <Dropdown.Root
                            label={"Estado*"}
                            mode={DropdownModes.SingleSelect}
                            size={DropdownSizes.Md}
                            disabled
                            selectedOption={
                              address.state
                                ? {
                                    id: address.state,
                                    label: address.state,
                                    value: address.state,
                                  }
                                : null
                            }
                          />
                        </Box>
                      </Box>
                      <Box gap={"xs"} flex={1} alignItems="stretch">
                        <Box flex={0.3} flexDirection="column">
                          <TextField
                            fullWidth
                            error={!isFormDisabled && !!errors?.address?.number}
                            helperText={
                              !isFormDisabled && errors?.address?.number
                            }
                            onBlur={onAddressNumberBlur}
                            onChange={(event) =>
                              onAddressNumberChange(event.target.value)
                            }
                            label={"Número*"}
                            value={address.number || ""}
                            disabled={isFormDisabled}
                          />
                        </Box>
                        <Box flex={0.4} flexDirection="column">
                          <TextField
                            fullWidth
                            onChange={(event) =>
                              onAddressComplementChange(event.target.value)
                            }
                            label={"Complemento"}
                            value={address.complement || ""}
                            disabled={isFormDisabled}
                          />
                        </Box>
                      </Box>
                    </SC.AddressSectionAnimation>
                  )}
                  <Box flexDirection="column" alignSelf="flex-start">
                    <Box
                      alignItems="center"
                      $minWidth={0}
                      $borderRadius={theme.borders.radius.m}
                      $pr="xs3"
                      $border={
                        errors?.termsAccepted &&
                        `${theme.borders.width.s} solid ${theme.colors.feedback.error[50]}`
                      }
                    >
                      <Checkbox
                        checked={termsAccepted}
                        onChange={(_, checked) =>
                          onTermsAcceptedChange(checked)
                        }
                      />
                      <SC.AcceptTermsLabel
                        variant="body4"
                        color="neutral.30"
                        weight={600}
                      >
                        {`Declaro que li e concordo com o `}
                        <button onClick={onGetContractClick}>
                          contrato de grupo econômico
                        </button>
                      </SC.AcceptTermsLabel>
                    </Box>
                    {!!errors?.termsAccepted && (
                      <SC.HelperText>
                        <Icons
                          color={theme.colors.feedback.error[50]}
                          name="IconInfoCircle"
                          size={18}
                        />
                        Você precisa declarar que leu e concorda com o contrato
                        para continuar
                      </SC.HelperText>
                    )}
                  </Box>

                  <Box flex={1}>
                    <SC.AddMembersButton
                      variant="secondary"
                      variantType="default"
                      loading={addCompanyLoading}
                      onClick={onAddCompanyClick}
                    >
                      Salvar
                      <Icons
                        name="IconCheck"
                        color={theme.colors.secondary[50]}
                        size={16}
                      />
                    </SC.AddMembersButton>
                  </Box>
                </Box>
              </Box>
            </MaskedScrollArea>
          </SC.LeftSection>
          <SC.RightSection>
            <Box
              flexDirection="column"
              $minHeight={"100%"}
              $border={`${theme.borders.width.xs2} solid ${theme.colors.neutral[90]}`}
              $borderRadius={theme.borders.radius.m}
              $py="xs2"
            >
              <MaskedScrollArea>
                <Box
                  flexDirection="column"
                  gap="xs2"
                  $minHeight={"100%"}
                  $px="xs2"
                >
                  <Box gap="xs5">
                    <Typography.Body4 color="neutral.20" weight={600}>
                      Empresas adicionadas
                    </Typography.Body4>
                    <div>
                      <Tag as="span" variant="gray" size="extra-small">
                        {totalCompaniesAdded}
                      </Tag>
                    </div>
                  </Box>
                  <SearchField
                    label={"Buscar"}
                    onChange={(e) => {
                      onSearchCompaniesTermChange(e.target.value);
                    }}
                    inputSize="md"
                    onClear={() => onSearchCompaniesTermChange("")}
                  />
                  <Box flexDirection="column" flex={1}>
                    {filteredAddedCompanies?.map(
                      ({
                        registrationNumber,
                        id,
                        legalName,
                        deleteEnabled,
                      }) => (
                        <AddedCompany
                          key={id}
                          companyName={legalName}
                          registrationNumber={registrationNumberFormatter(
                            registrationNumber
                          )}
                          onDeleteClick={
                            deleteEnabled
                              ? () => onDeleteCompanyClick(id)
                              : undefined
                          }
                        />
                      )
                    )}
                  </Box>
                </Box>
              </MaskedScrollArea>
            </Box>
          </SC.RightSection>
        </SC.Grid>
      </SC.ResponsiveContainer>
      <GenerateContractLoadingModal
        open={isGenerateContractLoadingModalOpen}
        onClose={onGenerateContractLoadingModalClose}
      />
      <DeleteCompanyModal
        open={isDeleteCompanyModalOpen}
        onSubmit={onDeleteCompanySubmit}
        onClose={onDeleteCompanyModalClose}
      />
    </>
  );
};
